import Redis from 'ioredis';

import config from 'turtle/config';
import logger from 'turtle/logger';

const MILLIS_TO_UPLOAD_LOGS = 3000;
export const MILLIS_CONNECTION_TIMEOUT = 10000;

export enum RedisClient {
  Subscriber = 'REDIS_CLIENT_SUBSCRIBER',
  Default = 'REDIS_CLIENT_DEFAULT',
  Configuration = 'REDIS_CLIENT_CONFIGURATION',
}

interface IClientConfig {
  [key: string]: {
    url: string;
    caCertificate?: string;
  };
}

const clientConfigs: IClientConfig = {
  [RedisClient.Subscriber]: config.redis.main,
  [RedisClient.Default]: config.redis.main,
  [RedisClient.Configuration]: config.redis.turtle,
};

export function connect(timeoutMs: number, type: string): Promise<any> {
  return new Promise((resolve, reject) => {
    const { url, caCertificate } = clientConfigs[type];
    const redisClient = new Redis(url, {
      maxRetriesPerRequest: 2,
      ...caCertificate && {
        tls: { ca: caCertificate },
      },
    });
    const timer = setTimeout(() => reject(new Error('Timeout at connecting to Redis')), timeoutMs);
    redisClient.on('ready', () => {
      clearTimeout(timer);
      resolve(redisClient);
    });
    redisClient.on('error', (err: Error) => {
      clearTimeout(timer);
      reject(err);
    });
  });
}

interface IRedisClients {
  [key: string]: any;
}

const redisClients: IRedisClients = {
  [RedisClient.Subscriber]: null,
  [RedisClient.Default]: null,
  [RedisClient.Configuration]: null,
};

export async function getRedisClient(type = RedisClient.Default) {
  if (!redisClients[type]) {
    try {
      redisClients[type] = await connect(MILLIS_CONNECTION_TIMEOUT, type);
    } catch (err) {
      logger.error({ err });
    }
  }
  return redisClients[type];
}

export async function checkIfCancelled(jobId: string) {
  try {
    const redis = await getRedisClient();
    return await redis.get(`jobs:cancelled:${jobId}`);
  } catch (err) {
    if (config.deploymentEnv === 'development') {
      logger.warn('Did you turn on redis server? Run `yarn start-docker` in $EXPO_UNIVERSE_DIR/server/www');
    }
    logger.error({ err });
    return false;
  }
}

export async function registerListener(jobId: string, deleteMessage: any) {
  try {
    const redis = await getRedisClient(RedisClient.Subscriber);
    redis.subscribe('jobs:cancelled');
    redis.on('message', async (_: any, message: any) => {
      if (message === jobId) {
        logger.info({ lastBuildLog: true }, 'Job cancelled - killing process');
        await deleteMessage();
        await logger.cleanup();
        setTimeout(() => process.exit(1), MILLIS_TO_UPLOAD_LOGS);
      }
    });
  } catch (err) {
    logger.info('Couldn\'t connect to Redis - job will be continued even if cancelled during build');
    logger.error(err);
  }
}

export async function unregisterListeners() {
  try {
    const redis = await getRedisClient();
    redis.removeAllListeners('message');
  } catch (err) {
    logger.error(err);
  }
}
